import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, MessageCircle, ArrowRight, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { MobileCTA } from '../components/MobileCTA';
import { businessConfig, trackPhoneClick } from '../config/business';

export default function ContactPage() {
  const { contact, company } = businessConfig;

  const contactMethods = [
    {
      icon: Phone,
      title: 'Call Us',
      description: 'Speak directly with our team',
      value: contact.phone,
      action: contact.phoneLink,
      buttonText: 'Call Now',
      primary: true,
      onClick: () => trackPhoneClick('Contact - Call')
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      description: 'Message us anytime',
      value: contact.whatsapp,
      action: contact.whatsappUrl,
      buttonText: 'Open WhatsApp',
      external: true
    },
    {
      icon: Mail,
      title: 'Email',
      description: 'For general enquiries',
      value: contact.email,
      action: `mailto:${contact.email}`,
      buttonText: 'Send Email'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Contact Thrifty Move | Melbourne Removalists | 1800 233 023</title>
        <meta name="description" content="Contact Thrifty Move Melbourne removalists. Call 1800 233 023 or message us on WhatsApp. Professional moving from $130/hr. Get a free quote today." />
        <link rel="canonical" href="https://thrifty-move.com.au/contact" />
        <meta property="og:title" content="Contact Thrifty Move | Melbourne Removalists" />
        <meta property="og:description" content="Contact Melbourne removalists. Call 1800 233 023, WhatsApp, or email us. From $130/hr including 2 removalists." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thrifty-move.com.au/contact" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "mainEntity": {
              "@type": "MovingCompany",
              "name": "Thrifty Move",
              "telephone": "1800 233 023",
              "email": "info@thrifty-move.com.au",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Oakleigh South",
                "addressRegion": "VIC",
                "postalCode": "3167",
                "addressCountry": "AU"
              }
            }
          })}
        </script>
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="bg-gray-900 pt-28 md:pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Contact Us
            </h1>
            <p className="text-xl text-gray-300">
              Ready to move? Have a question? We're here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* Get a Quote CTA - Primary */}
            <Card className="border-2 border-yellow-500 bg-yellow-50 mb-10">
              <CardContent className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Need a Moving Quote?</h2>
                    <p className="text-gray-700">
                      Use our online quote system to list your items and get accurate pricing for your move.
                    </p>
                  </div>
                  <Link to="/quote">
                    <Button className="w-full md:w-auto bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold px-8 py-4 text-lg whitespace-nowrap">
                      <ArrowRight className="mr-2 w-5 h-5" />
                      Get a Free Quote
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Contact Methods Grid */}
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Get in Touch</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              {contactMethods.map((method, i) => {
                const Icon = method.icon;
                return (
                  <Card key={i} className={`border-2 ${method.primary ? 'border-yellow-400' : 'border-gray-200'}`}>
                    <CardContent className="p-6 text-center">
                      <div className={`w-14 h-14 ${method.primary ? 'bg-yellow-500' : 'bg-gray-100'} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                        <Icon className={`w-7 h-7 ${method.primary ? 'text-gray-900' : 'text-gray-600'}`} />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{method.title}</h3>
                      <p className="text-gray-500 text-sm mb-3">{method.description}</p>
                      <p className="font-semibold text-gray-900 mb-4" data-phone-number={method.title === 'Call Us' ? 'true' : undefined}>{method.value}</p>
                      <a 
                        href={method.action}
                        target={method.external ? '_blank' : undefined}
                        rel={method.external ? 'noopener noreferrer' : undefined}
                        onClick={method.onClick}
                        data-phone-number={method.title === 'Call Us' ? 'true' : undefined}
                      >
                        <Button 
                          className={`w-full ${method.primary ? 'bg-yellow-500 hover:bg-yellow-400 text-gray-900' : 'bg-gray-900 hover:bg-gray-800 text-white'} font-semibold`}
                        >
                          {method.buttonText}
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Business Details */}
            <Card className="border border-gray-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Business Details</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900">Location</p>
                        <p className="text-gray-600">{contact.location}</p>
                        <p className="text-gray-500 text-sm">{contact.serviceArea}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900">Operating Hours</p>
                        <p className="text-gray-600">7 days a week</p>
                        <p className="text-gray-500 text-sm">Contact us to check availability</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="font-medium text-gray-900">Business Name</p>
                      <p className="text-gray-600">{company.legalName}</p>
                      <p className="text-gray-500 text-sm">Trading as {company.tradingAs}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">ABN</p>
                      <p className="text-gray-600">{company.abn}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Note about enquiries */}
            <p className="text-center text-gray-500 text-sm mt-8">
              For moving enquiries, we recommend using our <Link to="/quote" className="text-yellow-600 hover:text-yellow-700 font-medium">online quote system</Link> to get accurate pricing based on your inventory.
            </p>

          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Get Moving?
          </h2>
          <p className="text-gray-300 mb-8 max-w-xl mx-auto">
            Start with a free quote or give us a call to discuss your move.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote">
              <Button className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold px-10 py-5 text-lg">
                <ArrowRight className="mr-2 w-5 h-5" />
                Get a Free Quote
              </Button>
            </Link>
            <a href={contact.phoneLink} onClick={() => trackPhoneClick('Contact Footer - Call')} data-phone-number="true">
              <Button variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-gray-900 font-bold px-10 py-5 text-lg">
                <Phone className="mr-2 w-5 h-5" />
                <span data-phone-number="true">{contact.phone}</span>
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <MobileCTA />
    </div>
  );
}
